import { useCallback, useMemo } from "react";
import { UserLoggedInNavigatorScreenProps } from "../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { LazyQueryNetworkBoundrary } from "../../components/mobile/QueryNetworkBoundrary";
import { graphql, useLazyLoadQuery, usePaginationFragment } from "react-relay";
import { SignalsQuery } from "./__generated__/SignalsQuery.graphql";
import { SignalsList_team$key } from "./__generated__/SignalsList_team.graphql";
import { SignalListItem } from "./SignalListItem";
import { ActivityIndicator, SectionList } from "react-native";

import { AHSeparator, AHText, Box, useLayoutContext } from "arkh";
import { withWebModalStack } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModalStack";
import { useNavigationHeader } from "../../common/navigationHeaderOptions";
import { useIntlTokens } from "../../components/mobile/useIntlTokens";
import { SignalsEmptyScreen } from "./SignalsEmptyScreen";
import { usePersistedEmployeeId } from "@/src/OS/Login/usePersistedEmployeeId";

export const SignalsScreen = withWebModalStack(
  (props: UserLoggedInNavigatorScreenProps<"Signals">) => {
    const intl = useIntlTokens();
    useNavigationHeader({
      type: "CLOSE",
      title: intl.Signals,
      onClose() {
        props.navigation.goBack();
      },
    });

    const openSignal = useCallback(
      (id: string) => {
        props.navigation.navigateDeprecated("SignalDetail", { signalId: id });
      },
      [props.navigation]
    );
    const render = useCallback(
      (fetchKey: string) => (
        <Signals
          fetchKey={fetchKey}
          teamId={props.route.params.teamId}
          openSignal={openSignal}
        />
      ),
      [openSignal, props.route.params.teamId]
    );
    return (
      <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
        {render}
      </LazyQueryNetworkBoundrary>
    );
  },
  true
);

function Signals(props: {
  teamId: string;
  fetchKey: string;
  openSignal: (id: string) => void;
}) {
  const { currentEmployeeId } = usePersistedEmployeeId();
  const data = useLazyLoadQuery<SignalsQuery>(
    graphql`
      query SignalsQuery(
        $teamId: ID!
        $first: Int
        $after: String
        $employeeId: ID
      ) {
        store {
          team(id: $teamId) {
            ...SignalsList_team
          }
        }
      }
    `,
    {
      teamId: props.teamId,
      employeeId: currentEmployeeId,
    },
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  if (!data.store.team) return null;

  return <SignalList team={data.store.team} openSignal={props.openSignal} />;
}

function SignalList(props: {
  team: SignalsList_team$key;
  openSignal: (id: string) => void;
}) {
  const { data, hasNext, loadNext, isLoadingNext } = usePaginationFragment(
    graphql`
      fragment SignalsList_team on Team
      @refetchable(queryName: "SignalList_teamQuery") {
        signals(first: $first, after: $after)
          @connection(key: "SignalScreenList_signals") {
          edges {
            node {
              employeeOpened(employeeId: $employeeId)
              ...SignalListItem_signal
            }
          }
        }
      }
    `,
    props.team
  );

  const onEndReached = useCallback(() => {
    if (hasNext) {
      loadNext(40);
    }
  }, [hasNext, loadNext]);

  const { space } = useLayoutContext();

  const containerStyle = useMemo(
    () => ({
      paddingHorizontal: space.medium,
    }),
    [space.medium]
  );

  const intl_tokens = useIntlTokens();

  const isSignalOpened = useCallback(
    (edge: { node: { employeeOpened: boolean } }) => edge.node.employeeOpened,
    []
  );

  const sections = useMemo(() => {
    if (!data.signals || !data.signals.edges) {
      return [];
    }

    const unread = data.signals.edges.filter((edge) => !isSignalOpened(edge));
    const opened = data.signals.edges.filter((edge) => isSignalOpened(edge));
    return [
      ...(unread.length > 0
        ? [
            {
              key: "unread",
              title: intl_tokens.Unread,
              data: unread,
            },
          ]
        : []),
      ...(opened.length > 0
        ? [
            {
              key: "opened",
              title: intl_tokens.Opened,
              data: opened,
            },
          ]
        : []),
    ];
  }, [data.signals, intl_tokens.Unread, intl_tokens.Opened, isSignalOpened]);

  if (sections.length === 0) {
    return <SignalsEmptyScreen />;
  }

  return (
    <SectionList
      contentContainerStyle={containerStyle}
      ItemSeparatorComponent={AHSeparator}
      stickySectionHeadersEnabled={false}
      sections={sections}
      renderItem={({ item, index, section }) => (
        <SignalListItem
          signal={item.node}
          isFirst={index === 0}
          isLast={index === section.data.length - 1}
          openSignal={props.openSignal}
        />
      )}
      renderSectionHeader={({ section }) => (
        <AHText spaceTop="medium" spaceBottom="small" spaceHorizontal="medium">
          {section.title}
        </AHText>
      )}
      onEndReached={onEndReached}
      ListFooterComponent={
        isLoadingNext ? (
          <Box spaceTop="medium">
            <ActivityIndicator size="large" />
          </Box>
        ) : null
      }
    />
  );
}
